// THE APP'S OWN WORDS FOR THE SCREENS AFTER FILING (WEB-PARITY-7; `docs/WEB_APP_PARITY.md` §2.15-2.17).
//
// Tyler, 2026-09-29: "the site should replicate exactly what the app has at this time" and "It should model the app
// perfectly". `/outcome` says what the app's "Log the outcome" sheet says (LogOutcomeView.swift); `/home` what its
// Properties tab and property page say (PropertiesTab.swift, HomeCard.swift, HomeDetailView.swift) — not its Home tab,
// whose figures are an EXAMPLE (ruling D5) — plus the Home tab's community card (HomeTab.swift), the one real figure on
// it; `/delete-account` what its Account screen says (AccountManagementView.swift). Every entry is the app's string
// literal on the line it cites (under `AppealAce/`), with the app's `\(…)` interpolations written `{name}`; nothing here
// is a new sentence, so each carries Tyler's standing instruction (`WEB_PARITY`) and no approval of its own.
// `web/test/afterFiling.test.tsx` reads every cited line and fails on any difference.
//
// ONE IS A CUT, AND THE CUT IS NAMED (`cut`), as WEB-PARITY-2B's are: the Properties card's draft line keeps "Finish your
// draft" and drops "· step {n} of {total}". The app counts its own seven steps from what the phone holds
// (`HomeCard.draftPhase`); the website's steps are the app's seven since WEB-PARITY-5 (ruling D3), but the snapshot does
// not carry what `draftPhase` reads, so no step number here could be the app's.
//
// TWO APP SENTENCES ARE LEFT OUT, EACH FOR A NAMED REASON (R112, the route half):
//   - the Properties tab's empty state body, "Save a property and start your first appeal — we'll track every year here."
//     (PropertiesTab.swift:173), names saving a property, which the website cannot do. The property page's own empty line
//     (HomeDetailView.swift:64) is used instead: it names only starting an appeal, which the website does.
//   - "How did it go? Log the outcome" (HomeCard.swift:122) is the app's line for an EXPORTED appeal. The website records
//     no export (the PDF and the copied text are the owner's own), so its badge never says "Exported"
//     (WEB-PARITY-1B, HM-1) and this line, which belongs to that badge, is not shown either.
//
// A SENTENCE THE SERVER STATES IS NOT HERE, and neither is a figure: every amount these sentences carry is the server's
// (`AppealSummary`, the snapshot, `OutcomeStats`), or the owner's own typing run through the server's own rules
// (`@v2-outcome-rules`).

import { WEB_PARITY, type AppWording } from "./heldCopy";

/** An app string on the website. `cut` names the words dropped, when the text is the literal's opening only.
 *  `formerly` marks the one line the app has since REPLACED: its `appSource` is where the replacement now stands, `record`
 *  is the approved plan's row that quotes these exact words as the app's, and `replacedBy` names the sentence that took
 *  their place. test/afterFiling.test.tsx holds all three to the files they name. */
export interface AfterFilingWording extends AppWording {
  readonly cut: string | null;
  readonly formerly?: { readonly record: string; readonly replacedBy: string };
}

function app(appSource: string, text: string, cut: string | null = null): AfterFilingWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource, cut };
}

/** A line the app said until `main`'s R175 replaced it, kept where the website's own switch still needs it. */
function formerApp(appSource: string, text: string, record: string, replacedBy: string): AfterFilingWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource, cut: null, formerly: { record, replacedBy } };
}

const LOG = "Features/Outcome/LogOutcomeView.swift";
const CARD = "Shared/HomeCard.swift";
const DETAIL = "Features/Home/HomeDetailView.swift";
const PROPERTIES = "Features/Tabs/PropertiesTab.swift";
const HOME_TAB = "Features/Tabs/HomeTab.swift";
const ACCOUNT = "Features/Account/AccountManagementView.swift";

export const AFTER_FILING_WORDING = {
  // ── `/outcome`: the app's "Log the outcome" sheet (OC-1 to OC-8) ──
  "outcome.navTitle": app(`${LOG}:77`, "Log the outcome"),
  "outcome.intro": app(
    `${LOG}:65`,
    "Logging the result helps you track your savings — and, if you choose to share it anonymously, helps other homeowners see what's working.",
  ),
  "outcome.cancel": app(`${LOG}:82`, "Cancel"),
  "outcome.hero.won": app(`${LOG}:105`, "Appeal granted"),
  "outcome.hero.partiallyWon": app(`${LOG}:105`, "Partially granted"),
  "outcome.hero.saved": app(`${LOG}:109`, "{amount}/yr saved"),
  "outcome.hero.reducedTo": app(`${LOG}:115`, "Reduced to {final} · −{reduction} off your assessment"),
  "outcome.picker.title": app(`${LOG}:132`, "WHAT HAPPENED?"),
  "outcome.final.title": app(`${LOG}:173`, "What value did the county land on?"),
  "outcome.final.field": app(`${LOG}:174`, "Final assessed value"),
  "outcome.final.lower": app(`${LOG}:178`, "≈ {reduction} lower than your {assessed}"),
  "outcome.final.saved": app(`${LOG}:182`, "about {amount}/yr saved · estimate at {rate}"),
  "outcome.final.rateLabel": app(`${LOG}:57`, "the ~{pct}% {state} average tax rate"),
  "outcome.final.higher": app(`${LOG}:192`, "That's {increase} HIGHER than your original {assessed} assessment."),
  "outcome.final.notBelow": app(
    `${LOG}:196`,
    "That's not below your original assessment — double-check the value the county set.",
  ),
  "outcome.next.filed.title": app(`${LOG}:221`, "What happens next"),
  "outcome.next.filed.body": app(
    `${LOG}:222`,
    "Your board or hearing officer will schedule a review — timing varies by county, typically a few weeks to a few months. The assessor's office may show up with its own evidence, so be ready to explain your comps. You'll usually get a written decision after the hearing, not on the spot.",
  ),
  "outcome.next.further.title": app(`${LOG}:230`, "You may still be able to go further"),
  "outcome.next.further.body": app(
    `${LOG}:231`,
    "Most states let you take a denied, partial, or increased result to a next level — a state board, tax tribunal, or court. These next-level appeals almost always run on a SHORT deadline of their own, often 30–90 days from this decision. Check your written decision or notice for the exact deadline and where to file — it's easy to miss if you wait.",
  ),
  "outcome.note.title": app(`${LOG}:241`, "Anything to remember? (optional)"),
  "outcome.note.placeholder": app(`${LOG}:242`, "e.g. \"Board agreed at the informal hearing.\""),
  "outcome.share.title": app(`${LOG}:248`, "Help other homeowners"),
  // J1 (ACCOUNTS_PLAN §3 J, approved 2026-09-30, "APP" there): the app's own line, shown only when the server answered
  // the recorded DECIDED result eligible for the returning-owner price (LogOutcomeView.save, :322-323).
  "outcome.returningThanks": app(`${LOG}:268`, "Thanks for logging it. Your next appeal is at the returning-owner price."),
  "outcome.done": app(`${LOG}:272`, "Done"),
  "outcome.save": app(`${LOG}:279`, "Save outcome"),

  // ── `/home`: the app's Properties tab (HM-1 to HM-5, HM-7) ──
  "home.navTitle": app(`${PROPERTIES}:43`, "Properties"),
  // The tab bar's name for the same screen, on the header's link to `/home` (WALK-FIX-1).
  "home.tab": app("App/MainTabView.swift:140", "Properties"),
  // `Home.title`: the address, or this when there is none (Models.swift:2854-2857).
  "home.untitled": app("Models/Models.swift:2856", "Untitled property"),
  // "Delete property" (WALK-FIX-2): the visible button under each property, its accessibility label, and the app's
  // confirmation (PropertiesTab.swift:56-73, :135-143), title and buttons verbatim. The app's MESSAGE under the title says
  // "…from this device", which is false on the website (it deletes from the account), so it is not here: the website
  // shows its own message instead, approved by Tyler 2026-10-05 (`DELETE_PROPERTY_MESSAGE`, routes/homeDrafts.ts), under
  // the app's title and buttons.
  "home.remove": app(`${PROPERTIES}:138`, "Remove"),
  "home.remove.label": app(`${PROPERTIES}:143`, "Remove property"),
  "home.delete.title": app(`${PROPERTIES}:59`, "Delete this property?"),
  "home.delete.confirm": app(`${PROPERTIES}:64`, "Delete property"),
  "home.delete.cancel": app(`${PROPERTIES}:68`, "Cancel"),
  "home.empty": app(`${DETAIL}:64`, "No appeals yet. Start one to build your letter."),
  "home.card.assessed": app(`${CARD}:40`, "Assessed"),
  "home.card.estSavings": app(`${CARD}:52`, "Est. tax savings"),
  "home.card.estSavingsValue": app(`${CARD}:52`, "≈ {amount}/yr"),
  "home.card.requested": app(`${CARD}:55`, "Requested"),
  "home.card.requestedNoLower": app(`${CARD}:55`, "Requested — {note}"),
  "home.card.wouldNotLower": app("Models/Models.swift:2292", "would not lower your bill"),
  "home.card.appeals": app(`${CARD}:69`, "{n} appeals"),
  "home.next.won": app(`${CARD}:109`, "Won — re-appeal to keep it low next year"),
  "home.next.filed": app(`${CARD}:110`, "Filed — awaiting the county's decision"),
  "home.next.denied": app(`${CARD}:111`, "Denied — you can re-appeal next year"),
  "home.next.raised": app(`${CARD}:112`, "Assessment increased — check if you can still escalate"),
  "home.next.withdrawn": app(`${CARD}:113`, "Withdrawn"),
  "home.next.draft": app(`${CARD}:120`, "Finish your draft", "· step {} of {}"),
  "home.next.generated": app(`${CARD}:121`, "Ready to export"),

  // ── `/home/:appealId`: the app's property page (HM-5) ──
  "home.page.property": app(`${DETAIL}:43`, "Property"),
  "home.page.assessedValue": app(`${DETAIL}:45`, "Assessed value"),
  "home.page.requestedReduction": app(`${DETAIL}:49`, "Requested reduction"),
  "home.page.appeals": app(`${DETAIL}:62`, "Appeals"),
  "home.page.beds": app(`${DETAIL}:211`, "{n} bd"),
  "home.page.baths": app(`${DETAIL}:212`, "{n} ba"),
  "home.page.sqft": app(`${DETAIL}:213`, "{n} sqft"),
  "home.row.year": app(`${DETAIL}:173`, "{year} appeal"),
  "home.row.increased": app(`${DETAIL}:187`, "Assessment increased"),
  "home.row.saved": app(`${DETAIL}:192`, "Saved −{amount}"),
  "home.row.estimated": app(`${DETAIL}:196`, "Est. −{amount} ({percent}%)"),
  "home.row.logOutcome": app(`${DETAIL}:121`, "Log the outcome"),
  "home.row.updateOutcome": app(`${DETAIL}:133`, "Update outcome"),
  "home.start": app(`${DETAIL}:153`, "Start an appeal"),
  "home.reappeal": app(`${DETAIL}:153`, "Re-appeal — new year"),

  // ── `/home`: the app's community card (HM-6), shown only when the server's aggregate is `available` ──
  "home.community.title": app(`${HOME_TAB}:189`, "PROppeal community"),
  "home.community.count": app(`${HOME_TAB}:196`, "{n}+"),
  "home.community.countLabel": app(`${HOME_TAB}:196`, "results logged"),
  "home.community.savedLabel": app(`${HOME_TAB}:199`, "est. saved per year*"),
  "home.community.footnote": app(
    `${HOME_TAB}:202`,
    "*Self-reported by homeowners who chose to share their results, estimated at the ~1.1% U.S. average tax rate. Not a prediction of your result.",
  ),

  // ── `/delete-account` and `/account/settings`: the app's Account screen (DA-1, DA-2; ACC-3b) ──
  "account.navTitle": app(`${ACCOUNT}:97`, "Account"),
  "account.email.appleRelay": app(`${ACCOUNT}:33`, "Apple Relay"),
  "account.cancel": app(`${ACCOUNT}:105`, "Cancel"),
  "account.delete.button": app(`${ACCOUNT}:73`, "Delete account"),
  // THE APP'S FOOTER UNTIL R175, which made it K12 in the app unconditionally (`AccountCopy.deleteFooter`, "Applies
  // now"). The website shows K12 only while WEB_ACCOUNTS_ENABLED is on (DeleteAccount.tsx): with it off a visitor has no
  // account shared with the app, and K12's "in the app and on app.proppeal.com" (its site name corrected by B6b,
  // 2026-10-09) would overstate. Kept, behavior unchanged, and recorded as the app's FORMER words at the merge of `main`
  // (R233, F-SYNC-3: whether the website shows K12 in both states is the orchestrator's ruling).
  "account.delete.footer": formerApp(
    `${ACCOUNT}:83`,
    "Permanently deletes your account and any remaining credits. This can't be undone.",
    "docs/ACCOUNTS_PLAN.md:827",
    "K12 (AccountCopy.deleteFooter)",
  ),
  "account.delete.alertTitle": app(`${ACCOUNT}:107`, "Delete your account?"),
  "account.delete.confirm": app(`${ACCOUNT}:108`, "Delete"),
  "account.delete.cancel": app(`${ACCOUNT}:116`, "Cancel"),
} as const satisfies Record<string, AfterFilingWording>;

export type AfterFilingId = keyof typeof AFTER_FILING_WORDING;

/** The words of one entry. The element that shows them carries `data-app-copy` with the id. */
export function afterText(id: AfterFilingId): string {
  return AFTER_FILING_WORDING[id].text;
}

/** An entry with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function afterTemplate(id: AfterFilingId, values: Readonly<Record<string, string | number>>): string {
  return afterText(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`afterTemplate(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}
